import type { TFunction } from 'i18next'
import { ChevronDown, EyeOff, Play, User, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  CandidateDecision,
  type DecisionHandlers,
  type DecisionLabels,
} from '@/components/faces/CandidateDecision'
import { FaceActionNotice } from '@/components/faces/FaceActionNotice'
import { FaceTile } from '@/components/faces/FaceTile'
import { describeQualityFlags, formatScore, matchEvidence } from '@/components/faces/format'
import { MatchConditions } from '@/components/faces/MatchConditions'
import { MatchReferenceCard } from '@/components/faces/MatchReferenceCard'
import { MatchScore } from '@/components/faces/MatchScore'
import { QualityFlagBadges } from '@/components/faces/QualityFlagBadges'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { type FaceRow, leftOutOfUnassigned } from '@/features/document-overlay/faces-section'
import type { FaceActionErrorKey } from '@/features/face-matching/errors'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import type { DocumentFaceCandidate } from '@/services/api/photos'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'

const DETAIL = 'documents.detail'
/** The comparison reuses the photo-matching copy the star and the table use. */
const PHOTO_MATCHING = 'cases.investigate.entityResolution.photoMatching'

/** Which tone a face's row and its box share, as token classes only (slice 06). */
export const FACE_STATE_TONE = {
  confirmed: { row: 'border-success/60', badge: 'success' },
  suggested: { row: 'border-warning/60', badge: 'warning' },
  unassigned: { row: 'border-border', badge: 'muted' },
} as const satisfies Record<FaceRow['state'], { row: string; badge: string }>

/** What the analyst can do on a face's row. */
export interface FaceRowActions {
  onConfirm: (faceId: string, personId: string) => void
  onReject: (faceId: string, personId: string) => void
  /**
   * "Merge" on a confirmed face's candidate *personId* (ADR-0042): the caller
   * asks first, then merges the face's identity and the candidate's.
   */
  onMerge: (faceId: string, personId: string) => void
  /** "Create identity" on an unassigned face — opens the dialog for it (slice 07). */
  onCreateIdentity: (faceId: string) => void
}

export interface FacesOnPhotoProps {
  rows: readonly FaceRow[]
  /** Low-quality undecided faces, revealed behind one row. */
  collapsed: readonly FaceRow[]
  /** The face picked in this list or on the photo; its row is highlighted. */
  selectedFaceId: string | null
  /**
   * The identity the overlay was opened for (slice 08): the row of a face this
   * identity is suggested on unfolds the full comparison against it. Null when
   * the overlay was opened the ordinary way — then no row carries a reference
   * card.
   */
  focusPersonId: string | null
  /** A decision or a merge is in flight for this face — its buttons wait. */
  pendingFaceId: string | null
  /** The last refusal's i18n suffix, null when the last action went through. */
  errorKey: FaceActionErrorKey | null
  /** Where each person's identity page lives. */
  identityHref: (personId: string) => string
  onSelect: (faceId: string) => void
  /**
   * Omitted for a viewer (PRD-0032 SC-036): the rows still show every face and
   * its candidates, offer no decision, and the list says why.
   */
  actions?: FaceRowActions
  /**
   * "Play pass" on a face of a recording (PRD-0074): the player runs that pass. Given
   * only when a player is on the page; a photo's rows never show the control.
   */
  onPlayPass?: (faceId: string) => void
}

/** A row with something to decide: its candidates, or a face to create an identity from. */
const offersDecision = (row: FaceRow) => row.state === 'unassigned' || row.candidates.length > 0

/**
 * Every face found on this photo, with its state (PRD-0031 slice 06).
 *
 * Confirmed faces name their person and link to them, suggested ones offer up to
 * three candidates with the similarity and the *suggested match* band from the
 * API, and unassigned ones show the crop with a "no match" hint. A confirmed face
 * may list candidates too: each is another identity the face's person may be, and
 * it offers the merge of the two instead of a confirm (ADR-0042). Selecting a row
 * is the same selection the face layer draws, so the list and the photo point at
 * one face.
 *
 * Opened from a star (slice 08), the row of the face that star was built around
 * unfolds the whole comparison against that identity — reference card, similarity,
 * the four ADR-0040 conditions with their figures, quality and status — whether or
 * not that identity is the face's strongest candidate.
 *
 * Pure: the rows, the pending face and the refusal all arrive as props, the
 * caller builds the href, and the score and the band come from the API — the
 * ADR-0040 thresholds never reach the UI. Whether the caller may decide arrives
 * the same way: as the `actions`, or their absence.
 */
export function FacesOnPhoto({
  rows,
  collapsed,
  selectedFaceId,
  focusPersonId,
  pendingFaceId,
  errorKey,
  identityHref,
  onSelect,
  actions,
  onPlayPass,
}: FacesOnPhotoProps) {
  const { t } = useTranslation()
  const [showCollapsed, setShowCollapsed] = useState(false)
  const shown = showCollapsed ? [...rows, ...collapsed] : rows
  // Said only where a face would have offered a decision, folded rows included.
  const readOnly = !actions && [...rows, ...collapsed].some(offersDecision)

  return (
    <div className="flex flex-col gap-2">
      <FaceActionNotice copy={`${DETAIL}.faces`} errorKey={errorKey} readOnly={readOnly} compact />
      <ul className="flex flex-col gap-2">
        {shown.map(row => (
          <FaceRowItem
            key={row.faceId}
            row={row}
            isSelected={row.faceId === selectedFaceId}
            isPending={row.faceId === pendingFaceId}
            list={{ focusPersonId, identityHref, onSelect, actions, onPlayPass }}
          />
        ))}
      </ul>
      {collapsed.length > 0 && !showCollapsed ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start text-muted-foreground"
          onClick={() => setShowCollapsed(true)}
        >
          <ChevronDown className="size-4" aria-hidden />
          {t(`${DETAIL}.faces.moreFaces`, { count: collapsed.length })}
        </Button>
      ) : null}
    </div>
  )
}

/** One face's row; *list* is what every row of the list shares. */
function FaceRowItem({
  row,
  isSelected,
  isPending,
  list,
}: {
  row: FaceRow
  isSelected: boolean
  isPending: boolean
  list: Pick<
    FacesOnPhotoProps,
    'focusPersonId' | 'identityHref' | 'onSelect' | 'actions' | 'onPlayPass'
  >
}) {
  const { t } = useTranslation()
  const tone = FACE_STATE_TONE[row.state]
  const { focusPersonId, identityHref, onSelect, actions, onPlayPass } = list
  // The identity the star was built for, however far down this face's list it
  // sits. Its comparison replaces its compact line, so nothing shows twice.
  const focused = focusPersonId
    ? (row.candidates.find(candidate => candidate.person_id === focusPersonId) ?? null)
    : null
  const others = row.candidates.filter(candidate => candidate !== focused)
  return (
    <li>
      {/* The whole row is the selection target, so hovering or clicking anywhere
          on it moves the highlight on the photo. */}
      <div
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        data-face-id={row.faceId}
        data-face-state={row.state}
        data-selected={isSelected ? 'true' : 'false'}
        onClick={() => onSelect(row.faceId)}
        onMouseEnter={() => onSelect(row.faceId)}
        onFocus={() => onSelect(row.faceId)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect(row.faceId)
          }
        }}
        className={cn(
          'flex flex-col gap-2 rounded-md border bg-card px-2 py-2 text-sm text-foreground transition-colors',
          tone.row,
          isSelected && 'border-primary ring-1 ring-primary'
        )}
      >
        <div className="flex items-center gap-3">
          <FaceTile url={row.cropUrl} alt={t(`${DETAIL}.faces.cropAlt`)} size="sm" />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <Badge variant={tone.badge as 'success' | 'warning' | 'muted'}>
              {t(`${DETAIL}.faces.state.${row.state}`)}
            </Badge>
            {row.pageNumber !== null ? (
              <span className="text-xs text-muted-foreground">
                {t(`${DETAIL}.faces.onPage`, { page: row.pageNumber })}
              </span>
            ) : null}
            {row.tStart !== null && row.tEnd !== null ? (
              <span
                className="text-xs tabular-nums text-muted-foreground"
                data-pass-of={row.faceId}
              >
                {t(`${DETAIL}.faces.pass`, {
                  from: formatVideoTime(row.tStart),
                  to: formatVideoTime(row.tEnd),
                })}
              </span>
            ) : null}
          </span>
          {onPlayPass && row.tStart !== null ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-label={t(`${DETAIL}.faces.playPass`)}
              title={t(`${DETAIL}.faces.playPass`)}
              data-play-pass={row.faceId}
              className="shrink-0 max-md:size-9 max-md:p-0"
              onClick={event => {
                event.stopPropagation()
                onPlayPass(row.faceId)
              }}
            >
              <Play className="size-4" aria-hidden />
              <span className="max-md:sr-only">{t(`${DETAIL}.faces.playPass`)}</span>
            </Button>
          ) : null}
        </div>

        {leftOutOfUnassigned(row) ? <QualityFloorNote flags={row.qualityFlags} /> : null}

        {row.state === 'confirmed' && row.personId ? (
          <PersonLink href={identityHref(row.personId)} name={row.displayName} />
        ) : null}

        {focused ? (
          <FocusedComparison
            candidate={focused}
            row={row}
            isPending={isPending}
            actions={actions}
          />
        ) : null}

        {row.state !== 'unassigned' && others.length > 0 ? (
          <CandidateLines row={row} candidates={others} isPending={isPending} actions={actions} />
        ) : null}

        {row.state === 'unassigned' ? (
          <span className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">{t(`${DETAIL}.faces.noMatch`)}</span>
            {actions ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-create-identity-for={row.faceId}
                disabled={isPending}
                onClick={event => {
                  event.stopPropagation()
                  actions.onCreateIdentity(row.faceId)
                }}
              >
                <UserPlus className="size-4" aria-hidden />
                {t(`${DETAIL}.faces.createIdentity`)}
              </Button>
            ) : null}
          </span>
        ) : null}
      </div>
    </li>
  )
}

/**
 * Why Identities does not offer this pass under Unassigned faces (PRD-0074 SC-008): its quality
 * warnings put it below the floor. The decision stays possible on this row.
 */
function QualityFloorNote({ flags }: { flags: readonly string[] }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-2 text-xs text-muted-foreground">
      <EyeOff className="mt-px size-3.5 shrink-0" aria-hidden />
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span>{t(`${DETAIL}.faces.belowQualityFloor`)}</span>
        <QualityFlagBadges flags={flags} />
      </span>
    </div>
  )
}

/** The confirmed person of a face, linking to their identity page. */
function PersonLink({ href, name }: { href: string; name: string | null }) {
  const { t } = useTranslation()
  return (
    <Link
      to={href}
      // Phone: a 36px tap target that takes no more room in the row, so a tap just
      // off the name still opens the identity instead of selecting the face.
      className="flex items-center gap-2 truncate text-sm text-primary hover:underline max-md:-my-2 max-md:py-2"
      // The row owns the selection; following the link must not also
      // re-select, or the navigation fights the highlight.
      onClick={event => event.stopPropagation()}
    >
      <User className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{name ?? t(`${DETAIL}.personsOnPhotoUnknown`)}</span>
    </Link>
  )
}

/**
 * The compact candidate lines of a face. On a confirmed face each one is another
 * identity the face's person may be, so the lines say so and offer the merge.
 */
function CandidateLines({
  row,
  candidates,
  isPending,
  actions,
}: {
  row: FaceRow
  candidates: DocumentFaceCandidate[]
  isPending: boolean
  actions?: FaceRowActions
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1">
      {row.state === 'confirmed' ? (
        <span className="text-xs text-muted-foreground">
          {t(`${DETAIL}.faces.mergeCandidates`)}
        </span>
      ) : null}
      <ul className="flex flex-col gap-2">
        {candidates.map(candidate => (
          <li
            key={candidate.person_id}
            data-candidate-of={row.faceId}
            className="flex flex-wrap items-center gap-2"
          >
            <span className="min-w-0 flex-1 truncate">
              {candidate.display_name ?? t(`${DETAIL}.personsOnPhotoUnknown`)}
            </span>
            <MatchScore
              score={formatScore(candidate.score)}
              suggestedMatch={candidate.suggested_match}
            />
            {actions ? (
              <CandidateDecision
                assigned={row.state === 'confirmed'}
                disabled={isPending}
                on={bindRowActions(actions, row.faceId, candidate.person_id)}
                labels={railDecisionLabels(t)}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * A row's handlers for one candidate, the face and the identity bound. On a
 * confirmed face the candidate proposes that the two identities are one person,
 * so it merges instead of confirming (ADR-0042).
 */
function bindRowActions(
  actions: FaceRowActions,
  faceId: string,
  personId: string
): DecisionHandlers {
  return {
    confirm: () => actions.onConfirm(faceId, personId),
    reject: () => actions.onReject(faceId, personId),
    merge: () => actions.onMerge(faceId, personId),
  }
}

/** The rail keeps its own words — a short "Merge" fits its narrow column. */
function railDecisionLabels(t: TFunction): DecisionLabels {
  return {
    confirm: t(`${DETAIL}.faces.confirm`),
    reject: t(`${DETAIL}.faces.reject`),
    merge: t(`${DETAIL}.faces.merge`),
  }
}

/**
 * The full comparison of one face against the identity the overlay was opened
 * for (slice 08): who that identity is, the similarity, the four ADR-0040
 * conditions with their figures, the face's quality warnings and its state, and
 * the decision — the merge, when the face is already confirmed to somebody else
 * (ADR-0042). It replaces what the match modal used to show, in the row of the
 * face itself.
 */
function FocusedComparison({
  candidate,
  row,
  isPending,
  actions,
}: {
  candidate: DocumentFaceCandidate
  /** The face compared; its quality warnings are the ADR-0040 quality condition. */
  row: FaceRow
  isPending: boolean
  actions?: FaceRowActions
}) {
  const { t } = useTranslation()
  // The identity's main photo sits behind the auth-gated files endpoint, and the
  // reference card takes a resolved URL — so it is resolved here, as `FaceTile`
  // does for a crop.
  const photoUrl = useAuthedImageUrl(candidate.photo_url)
  const score = formatScore(candidate.score)

  return (
    <section
      aria-label={t(`${DETAIL}.faces.comparison`)}
      data-comparison-with={candidate.person_id}
      className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-2"
    >
      <MatchReferenceCard
        compact
        identity={{
          displayName: candidate.display_name ?? t(`${DETAIL}.personsOnPhotoUnknown`),
          photoUrl,
          personId: candidate.person_id,
          referenceCount: candidate.reference_count,
        }}
      />
      <dl className="flex flex-col gap-3 text-sm">
        <div className="flex flex-col gap-1">
          <dt className="text-2xs uppercase tracking-wider text-muted-foreground">
            {t(`${PHOTO_MATCHING}.columns.similarity`)}
          </dt>
          <dd>
            <MatchScore
              score={score}
              suggestedMatch={candidate.suggested_match}
              className="text-base"
            />
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-2xs uppercase tracking-wider text-muted-foreground">
            {t(`${PHOTO_MATCHING}.comparison.conditions.title`)}
          </dt>
          <dd>
            <MatchConditions
              score={score}
              evidence={matchEvidence(candidate)}
              qualityFlags={row.qualityFlags}
            />
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-2xs uppercase tracking-wider text-muted-foreground">
            {t(`${PHOTO_MATCHING}.columns.quality`)}
          </dt>
          <dd className="text-muted-foreground">
            {describeQualityFlags(row.qualityFlags, t, t(`${PHOTO_MATCHING}.qualityFlags.none`))}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-2xs uppercase tracking-wider text-muted-foreground">
            {t(`${DETAIL}.faces.status`)}
          </dt>
          <dd>
            <Badge variant="muted" data-face-state={row.state}>
              {t(`${DETAIL}.faces.state.${row.state}`)}
            </Badge>
          </dd>
        </div>
      </dl>
      {actions ? (
        <CandidateDecision
          assigned={row.state === 'confirmed'}
          disabled={isPending}
          on={bindRowActions(actions, row.faceId, candidate.person_id)}
          labels={railDecisionLabels(t)}
          className="flex-wrap gap-2 [&>button]:flex-1"
        />
      ) : null}
    </section>
  )
}
